import type { UseQueryResult } from "@tanstack/react-query";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";

import { QueryKeys } from "@/constants";
import type { ProviderSearchSettingType } from "@/model/provider-search";
import type { GroupedResponse } from "@/pages/components/grouped-search-autocomplete";
import {
  fetchFreeTextProviderSearchSuggestion,
  fetchFreeTextProviderSearchSuggestionByCategory,
  fetchProviderSearchResult,
  fetchProviderSearchSetting,
} from "@/services/provider-search-service";
import type {
  IBaseProviderSearchPayload,
  IFreeTextProviderSearchSuggestionByCategoryPayload,
  IFreeTextProviderSearchSuggestionPayload,
  IProviderSearchPayload,
  NPIFreeTextType,
  ProviderFreeTextType,
  ProviderSearchFreeTextModifiersPayloadType,
  SpecialtyFreeTextType,
} from "@/types";
import useUserInfo from "./use-user-info";
import { useSelector } from "react-redux";
import type { RootState } from "@/store/store";

const useProviderSearchSetting = (): UseQueryResult<
  ProviderSearchSettingType[],
  Error
> => {
  const userInfo = useUserInfo();
  const settingPayload: IBaseProviderSearchPayload = {
    memberId: userInfo.memberId,
    groupId: userInfo.groupId,
    token: userInfo.accessToken,
  };
  return useQuery({
    queryKey: [QueryKeys.PROVIDER_SEARCH_SETTING],
    queryFn: async () => {
      const providerSearchSettingResponse =
        await fetchProviderSearchSetting(settingPayload);
      return providerSearchSettingResponse.data;
    },
    enabled:
      Boolean(settingPayload?.memberId) && Boolean(settingPayload?.groupId),
    staleTime: Infinity,
    retry: 1,
  });
};

const useFreeTextSearch = (
  freeTextSearchModifierPayload: ProviderSearchFreeTextModifiersPayloadType,
  providerSettings: ProviderSearchSettingType[],
  searchType?: string,
): UseQueryResult<
  GroupedResponse<
    NPIFreeTextType | ProviderFreeTextType | SpecialtyFreeTextType
  >,
  Error
> => {
  const userInfo = useUserInfo();
  const { location, network } = useSelector(
    (state: RootState) => state.providerSearch,
  );
  const freeTextSearchPayload: IFreeTextProviderSearchSuggestionPayload = {
    divisionId: userInfo.divisionId,
    planId: userInfo.planId,
    lat: location?.lat ?? 0,
    lon: location?.lng ?? 0,
    network,
    memberId: userInfo.memberId,
    groupId: userInfo.groupId,
    token: userInfo.accessToken,
    ...freeTextSearchModifierPayload,
  };
  return useQuery({
    queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT],
    queryFn: async () => {
      const specialityProviderSetting = providerSettings.filter((item) => {
        return item.type !== "triggerSearch";
      });
      const combinedSpecialitiesCategory = specialityProviderSetting.flatMap(
        (obj) =>
          obj.specialtyList.map((item) => ({
            ...item,
            categoryDesc: obj.categoryDesc,
            categoryCode: obj.categoryCode,
          })),
      );
      const searchTerm = new RegExp(
        searchType
          ? freeTextSearchPayload.resultPageSearchCriteria
          : freeTextSearchPayload.searchCriteria,
        "i",
      );
      const matchedSpeciality = combinedSpecialitiesCategory.filter((item) =>
        searchTerm.test(item.specialtyDesc),
      );
      const freeTextResponse = await fetchFreeTextProviderSearchSuggestion(
        freeTextSearchPayload,
      );
      if (matchedSpeciality.length > 0) {
        const suggestionsObj = {
          speciality: {
            data: matchedSpeciality,
            hasMore: false,
            title: "Specialty Detail",
          },
          ...freeTextResponse,
        };
        return suggestionsObj;
      } else {
        return freeTextResponse;
      }
    },
    enabled:
      !!location &&
      (searchType
        ? freeTextSearchPayload?.resultPageSearchCriteria?.length > 3
        : freeTextSearchPayload?.searchCriteria?.length >= 3),
    staleTime: 0,
    retry: 1,
  });
};

const useFreeTextSearchViewMore = () => {
  const userInfo = useUserInfo();
  const {
    location,
    viewMoreDialog,
    network,
    searchCriteria,
    category,
    specialty,
    taxonomyCode,
    searchType,
  } = useSelector((state: RootState) => state.providerSearch);
  const freeTextSearchViewMorePayload: IFreeTextProviderSearchSuggestionByCategoryPayload =
    {
      memberId: userInfo.memberId,
      groupId: userInfo.groupId,
      token: userInfo.accessToken,
      divisionId: userInfo.divisionId,
      planId: userInfo.planId,
      lat: location?.lat ?? 0,
      lon: location?.lng ?? 0,
      network,
      searchBy: viewMoreDialog.searchBy,
      offset: 0,
      searchCriteria: searchType
        ? searchCriteria
        : viewMoreDialog.viewMoreSearchCriteria,
      category: category?.categoryCode,
      speciality: specialty?.searchParameter,
      taxonomyCode,
      resultPageSearchCriteria: searchType
        ? viewMoreDialog?.viewMoreSearchCriteria
        : "",
      limit: 10,
    };
  return useInfiniteQuery({
    queryKey: [QueryKeys.PROVIDER_SEARCH_FREE_TEXT_VIEW_MORE],
    queryFn: async ({ pageParam = 0 }) => {
      const freeTextViewMoreResponse =
        await fetchFreeTextProviderSearchSuggestionByCategory({
          ...freeTextSearchViewMorePayload,
          offset: pageParam,
        });
      return freeTextViewMoreResponse;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined; // no more data
      const pageCount = allPages.length;
      return pageCount * 10;
    },
    enabled:
      !!location &&
      freeTextSearchViewMorePayload?.searchBy?.length > 0 &&
      (searchType
        ? (freeTextSearchViewMorePayload?.resultPageSearchCriteria ?? "")
            .length > 0
        : (freeTextSearchViewMorePayload?.searchCriteria ?? "").length > 0),
    retry: 1,
  });
};

const useProviderSearch = () => {
  const userInfo = useUserInfo();
  const { location, distance, network, dynamicPayload } = useSelector(
    (state: RootState) => state.providerSearch,
  );
  const providerSearchPayload: IProviderSearchPayload = {
    memberId: userInfo.memberId,
    divisionId: userInfo.divisionId,
    groupId: userInfo.groupId,
    planId: userInfo?.planId,
    token: userInfo.accessToken,
    distance: distance.value ?? 0,
    lat: location?.lat ?? 0,
    lon: location?.lng ?? 0,
    limit: 10,
    offset: 0,
    network,
    ...dynamicPayload,
  };
  return useInfiniteQuery({
    queryKey: [QueryKeys.PROVIDER_SEARCH],
    queryFn: async ({ pageParam = 0 }) => {
      const providerSearchResponse = await fetchProviderSearchResult({
        ...providerSearchPayload,
        offset: pageParam,
      });
      return providerSearchResponse;
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined;
      const pageCount = allPages.length;
      return pageCount * 10;
    },
    enabled: false,
    retry: 1,
  });
};

const useNearestProviderSearch = (
  nearestProviderSearchPayload: IProviderSearchPayload,
  searchCategory: string,
  isFromWidget: boolean | undefined = false,
) => {
  return useQuery({
    queryKey: [
      searchCategory,
      nearestProviderSearchPayload.lat,
      nearestProviderSearchPayload.lon,
    ],
    queryFn: async () => {
      const nearestProviderSearchResponse = await fetchProviderSearchResult(
        nearestProviderSearchPayload,
        isFromWidget,
      );
      return nearestProviderSearchResponse;
    },
    staleTime: Infinity,
    enabled:
      Boolean(nearestProviderSearchPayload?.lat) &&
      Boolean(nearestProviderSearchPayload?.lon),
    retry: 1,
  });
};

export {
  useProviderSearchSetting,
  useFreeTextSearch,
  useFreeTextSearchViewMore,
  useProviderSearch,
  useNearestProviderSearch,
};
